<template>
  <div class="organization-switcher-modal">
    <div v-if="isLoading" class="organization-switcher-modal__loading">
      <LoadingSpinner />
    </div>

    <template v-else>
      <div class="organization-switcher-modal__toolbar">
        <div class="organization-switcher-modal__search">
          <BaseIcon name="magnify" size="16" class="organization-switcher-modal__search-icon" />
          <input
            v-model.trim="searchQuery"
            type="text"
            :placeholder="$t('navigation.search_organizations_placeholder')"
            class="organization-switcher-modal__search-input"
          />
          <button
            v-if="searchQuery"
            type="button"
            class="organization-switcher-modal__search-clear"
            @click="searchQuery = ''"
          >
            <BaseIcon name="xmark" size="12" />
          </button>
        </div>
        <p class="organization-switcher-modal__meta">
          {{
            $t('navigation.organizations_count', {
              visible: filteredOrganizations.length,
              total: sortedOrganizations.length,
            })
          }}
        </p>
      </div>

      <div v-if="filteredOrganizations.length" class="organization-switcher-modal__list">
        <button
          v-for="org in filteredOrganizations"
          :key="org.id"
          type="button"
          class="organization-switcher-modal__item"
          :class="{ 'organization-switcher-modal__item--active': org.id === activeOrganizationId }"
          @click="handleOrgSwitch(org.id)"
        >
          <div class="organization-switcher-modal__item-icon">
            <img v-if="org.avatar" :src="org.avatar" :alt="org.name" />
            <AvatarElement
              v-else
              :backgroundImage="org.brand_settings?.icon"
              v-bind="
                !org.brand_settings?.icon && {
                  title: org.name ? org.name.substring(0, 2).toUpperCase() : '',
                  color: org.brand_settings?.primary_color,
                  textColor: org.brand_settings?.primary_accent_color,
                }
              "
            />
          </div>

          <div class="organization-switcher-modal__item-content">
            <h3>{{ org.name }}</h3>
            <p v-if="org.id === activeOrganizationId">
              {{ $t('navigation.current_organization') }}
            </p>
          </div>

          <BaseIcon
            v-if="org.id === activeOrganizationId"
            name="check"
            size="16"
            class="organization-switcher-modal__item-check"
          />
        </button>
      </div>

      <div v-else class="organization-switcher-modal__empty">
        <BaseIcon name="search" size="20" />
        <h4>{{ $t('navigation.no_organizations_found') }}</h4>
      </div>

      <div v-if="canCreateOrganization" class="organization-switcher-modal__actions">
        <Btn
          :text="$t('navigation.add_another_organization')"
          variant="outlined"
          icon="plus"
          @click="handleAddOrg"
        />
      </div>
    </template>
  </div>
</template>

<script>
  import { ref, computed } from 'vue';
  import { useUserStore } from '@/store/userStore';
  import AvatarElement from '@/components/partials/Avatar.vue';
  import LoadingSpinner from '@/components/partials/LoadingSpinner.vue';
  import Btn from '@/components/partials/Btn.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import { useUIStore } from '@/store/uiStore';
  import { useI18n } from '@/composables/useI18n';
  import { useRoleCheck } from '@/composables/useRoleCheck';
  import AddOrganizationForm from './AddOrganizationForm.vue';

  export default {
    name: 'OrganizationSwitcherModal',

    components: {
      AvatarElement,
      LoadingSpinner,
      Btn,
      BaseIcon,
      AddOrganizationForm,
    },
    setup(props, { emit }) {
      const userStore = useUserStore();
      const isLoading = ref(false);
      const searchQuery = ref('');
      const uiStore = useUIStore();
      const { t } = useI18n();
      const { isInstaller } = useRoleCheck();
      const canCreateOrganization = computed(() => !isInstaller.value);

      const sortedOrganizations = computed(() => {
        if (!userStore.organizations) return [];
        return [...userStore.organizations].sort((a, b) => {
          const nameA = a.name?.toLowerCase() || '';
          const nameB = b.name?.toLowerCase() || '';
          return nameA.localeCompare(nameB);
        });
      });

      const activeOrganizationId = computed(() => {
        return userStore.currentOrganization?.id || null;
      });

      const filteredOrganizations = computed(() => {
        const query = searchQuery.value.toLowerCase();
        if (!query) {
          return sortedOrganizations.value;
        }

        return sortedOrganizations.value.filter((organization) => {
          return organization.name?.toLowerCase().includes(query);
        });
      });

      async function handleOrgSwitch(orgId) {
        if (orgId === activeOrganizationId.value) {
          emit('submit');
          return;
        }

        isLoading.value = true;
        await userStore.switchOrganization(orgId);
        window.location.reload();
        isLoading.value = false;
        emit('submit');
      }

      function handleAddOrg() {
        const modalId = uiStore.addModal(AddOrganizationForm, {
          title: t('navigation.add_new_organization'),
          description: t('navigation.add_new_organization_description'),
          size: 'md',
          onSubmit: async () => {
            uiStore.closeModal(modalId);
            emit('submit');
          },
          onError: (errors) => {
            console.error('Form submission errors:', errors);
          },
        });
      }

      return {
        sortedOrganizations,
        filteredOrganizations,
        activeOrganizationId,
        handleOrgSwitch,
        handleAddOrg,
        canCreateOrganization,
        isLoading,
        searchQuery,
      };
    },
  };
</script>

<style scoped lang="scss">
  .organization-switcher-modal {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    &__loading {
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 12rem;
    }

    &__toolbar {
      position: sticky;
      top: 0;
      z-index: 1;
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      background: var(--white);
    }

    &__search {
      position: relative;
      display: flex;
      align-items: center;
    }

    &__search-icon {
      position: absolute;
      left: 0.75rem;
      color: var(--slate-400);
      pointer-events: none;
    }

    &__search-input {
      width: 100%;
      height: 2.625rem;
      border: 1px solid var(--slate-200);
      border-radius: 0.5rem;
      padding: 0 2.25rem 0 2.25rem;
      font-size: 0.875rem;
      color: var(--slate-800);
      transition:
        border-color 0.2s ease,
        box-shadow 0.2s ease;

      &::placeholder {
        color: var(--slate-400);
      }

      &:focus {
        outline: none;
        border-color: var(--brand-500);
        box-shadow: 0 0 0 3px var(--brand-100);
      }
    }

    &__search-clear {
      position: absolute;
      right: 0.5rem;
      width: 1.5rem;
      height: 1.5rem;
      border: none;
      border-radius: 9999px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      color: var(--slate-500);
      background: var(--slate-100);
      cursor: pointer;
      transition: all 0.2s ease;

      @include hover {
        background: var(--slate-200);
        color: var(--slate-700);
      }
    }

    &__meta {
      margin: 0;
      color: var(--slate-500);
      font-size: 0.75rem;
      font-weight: 500;
    }

    &__list {
      display: grid;
      gap: 0.75rem;
      grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
      max-height: min(60vh, 28rem);
      overflow-y: auto;
      padding-right: 0.25rem;
    }

    &__item {
      width: 100%;
      text-align: left;
      border: 1px solid var(--slate-200);
      background: var(--white);
      border-radius: 0.625rem;
      padding: 0.625rem;
      display: flex;
      align-items: center;
      gap: 0.75rem;
      cursor: pointer;
      transition:
        border-color 0.2s ease,
        box-shadow 0.2s ease,
        transform 0.2s ease;

      @include hover {
        border-color: var(--brand-300);
        box-shadow: 0 6px 18px -12px rgba(15, 23, 42, 0.35);
      }

      &:focus-visible {
        outline: 2px solid var(--brand-400);
        outline-offset: 2px;
      }

      &:active {
        transform: translateY(1px);
      }

      &--active {
        border-color: var(--brand-500);
        background: var(--brand-50);
      }
    }

    &__item-icon {
      width: 2.875rem;
      height: 2.875rem;
      border: 1px solid var(--slate-200);
      border-radius: 0.625rem;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;

      img {
        width: 2rem;
        height: 2rem;
        border-radius: 0.375rem;
      }
    }

    &__item-content {
      min-width: 0;
      flex: 1;

      h3 {
        margin: 0;
        font-size: 1rem;
        color: var(--slate-900);
        font-weight: 600;
        @include ellipsis(1);
      }

      p {
        margin: 0.25rem 0 0;
        font-size: 0.75rem;
        font-weight: 500;
        color: var(--brand-600);
      }
    }

    &__item-check {
      color: var(--brand-600);
      flex-shrink: 0;
    }

    &__empty {
      min-height: 10rem;
      border: 1px dashed var(--slate-300);
      border-radius: 0.75rem;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      gap: 0.5rem;
      color: var(--slate-500);
      text-align: center;

      h4 {
        margin: 0;
        font-size: 0.875rem;
        font-weight: 500;
        color: var(--slate-600);
      }
    }

    &__actions {
      display: flex;
      justify-content: flex-start;
      padding-top: 0.25rem;
    }
  }
</style>
